import React, { useEffect, useMemo, useState } from 'react';
import { ProtectedRoute } from '../../../components/ProtectedRoute';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import { RouteStatCard, RoutesTable, EditRouteModal, CreateRouteModal, AddOrderToRouteModal } from './components';
import { RouteItem, RouteStats, RouteStatusType, ScoreTrend } from './routesTypes';
import { useRoutesStore } from '../../../store/routesStore';
import { DeliveryRoute, RouteEfficiencyMetric } from '../../../services/routesApi';
import { RoutesOverviewMap } from '../../../components/RoutesOverviewMap';
import styles from './AdminRoutes.module.css';

function statusToUi(s: string): RouteStatusType {
  if (s === 'completed') return 'Completed';
  if (s === 'planned' || s === 'pending') return 'Planned';
  return 'Active';
}

function adapt(r: DeliveryRoute, metric?: RouteEfficiencyMetric): RouteItem {
  const profit = metric ? Math.min(100, Math.round(metric.revenue_per_mile * 10)) : 0;
  return {
    id: String(r.id),
    routeName: r.route_name,
    routeCode: `R${String(r.id).padStart(3, '0')}`,
    driver: r.assigned_driver_id ? `Driver #${r.assigned_driver_id}` : '—',
    status: statusToUi(r.status),
    profitScore: profit,
    scoreTrend: (profit >= 70 ? 'up' : 'down') as ScoreTrend,
    revenue: metric?.revenue_generated ?? 0,
    costPerStop: metric && metric.stops_completed > 0 ? metric.miles_driven / metric.stops_completed : 0,
    stopsPerHour: metric?.stops_per_day ?? 0,
    onTimePercent: metric?.completion_status === 'on_time' ? 100 : 80,
    totalStops: metric?.stops_completed ?? 0,
    estTime: metric ? `${Math.round(metric.total_time_minutes / 60)}h ${metric.total_time_minutes % 60}m` : '—',
  };
}

const AdminRoutes: React.FC = () => {
  const deliveryRoutes = useRoutesStore((s) => s.deliveryRoutes);
  const metrics = useRoutesStore((s) => s.metrics);
  const loading = useRoutesStore((s) => s.loading);
  const error = useRoutesStore((s) => s.error);
  const fetchDeliveryRoutes = useRoutesStore((s) => s.fetchDeliveryRoutes);
  const fetchMetrics = useRoutesStore((s) => s.fetchMetrics);
  const [editing, setEditing] = useState<DeliveryRoute | null>(null);
  const [creating, setCreating] = useState(false);
  const [addingTo, setAddingTo] = useState<DeliveryRoute | null>(null);

  useEffect(() => {
    fetchDeliveryRoutes();
    fetchMetrics();
  }, [fetchDeliveryRoutes, fetchMetrics]);

  const adapted = useMemo<RouteItem[]>(
    () => deliveryRoutes.map((r) => adapt(r, metrics.find((m) => m.route_id === r.id))),
    [deliveryRoutes, metrics]
  );

  const totalRevenue = metrics.reduce((sum, m) => sum + Number(m.revenue_generated || 0), 0);
  const avgRevenue = metrics.length > 0 ? totalRevenue / metrics.length : 0;
  const avgEfficiency = adapted.length > 0
    ? adapted.reduce((sum, r) => sum + r.onTimePercent, 0) / adapted.length
    : 0;

  const stats: RouteStats = {
    totalRoutes: String(deliveryRoutes.length),
    avgRouteRevenue: `$${(avgRevenue / 1000).toFixed(1)}K`,
    avgEfficiency: `${avgEfficiency.toFixed(0)}%`,
    activeDrivers: deliveryRoutes.filter((r) => r.assigned_driver_id !== null && r.is_active).length,
  };

  const onCloseCreate = () => {
    setCreating(false);
    fetchDeliveryRoutes();
  };
  const onCloseAdd = () => {
    setAddingTo(null);
    fetchDeliveryRoutes();
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>Delivery Routes</h2>
          <p className={styles.subtitle}>Create routes, attach picked orders as stops, and assign drivers.</p>
        </div>
        <button className={styles.createBtn} onClick={() => setCreating(true)}>
          <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
            <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
          </svg>
          Create Route
        </button>
      </div>

      <div className={styles.statsGrid}>
        <RouteStatCard title="Total Routes" value={stats.totalRoutes} icon="routes" color="blue" />
        <RouteStatCard title="Avg Route Revenue" value={stats.avgRouteRevenue} icon="revenue" color="green" />
        <RouteStatCard title="Avg Efficiency" value={stats.avgEfficiency} icon="efficiency" color="teal" />
        <RouteStatCard title="Active Drivers" value={String(stats.activeDrivers)} icon="drivers" color="red" />
      </div>

      <RoutesOverviewMap height={300} />

      <AsyncBoundary
        loading={loading && deliveryRoutes.length === 0}
        error={error}
        empty={!loading && adapted.length === 0}
        emptyMessage="No delivery routes yet"
        onRetry={() => fetchDeliveryRoutes()}
      >
        <RoutesTable
          items={adapted}
          onEdit={(id) => {
            const r = deliveryRoutes.find((x) => String(x.id) === id);
            if (r) setEditing(r);
          }}
          onAddOrder={(id) => {
            const r = deliveryRoutes.find((x) => String(x.id) === id);
            if (r) setAddingTo(r);
          }}
        />
      </AsyncBoundary>

      <EditRouteModal open={!!editing} route={editing} onClose={() => setEditing(null)} />
      <CreateRouteModal open={creating} onClose={onCloseCreate} />
      <AddOrderToRouteModal open={!!addingTo} route={addingTo} onClose={onCloseAdd} />
    </div>
  );
};

export const AdminRoutesPage: React.FC = () => (
  <ProtectedRoute requiredRoles={['admin', 'Driver', 'Warehouse Manager']}>
    <AdminRoutes />
  </ProtectedRoute>
);

export { AdminRoutes };
export default AdminRoutes;
